import React from 'react'
import clsx from 'clsx'
import {
  ClickAwayListener,
  Popper,
  Checkbox,
  Radio,
  Divider,
  CircularProgress
} from '@mui/material'

import { sortByRenderedComponentName } from '../../../utilities'
import useKeyListener from '@SonarCX/hooks/useKeyListener'
import useStyles from './styles'

const Element = ({ useRadio, ...props }) => {
  const Elem = useRadio ? Radio : Checkbox
  return <Elem {...props} />
}

const CheckboxDropdown = ({
  children,
  options = [],
  renderOption = val => val,
  onSelect,
  selected,
  limit,
  onClose,
  prevSelected = [],
  onSearch,
  useRadio,
  disablePortal,
  disabled,
  loading,
  anchorClass,
  closeOnSelect = false,
  TopChildren,
  startOpen = false
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [search, setShowSearch] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [filterSelected, setFilterSelected] = React.useState([])
  const [filterAgents, setFilterAgents] = React.useState([])
  const [shouldOpen, setShouldOpen] = React.useState(startOpen)

  const open = Boolean(anchorEl)
  const mainRef = React.useRef()

  const classes = useStyles({ disabled })

  const sortFunc = React.useCallback(
    (a, b) => {
      // Disables sorting if there is no query, to allow recents to show first
      if (query.length === 0) {
        return 0
      }

      return sortByRenderedComponentName(a, b)
    },
    [query]
  )

  const handleClick = React.useCallback(
    e => {
      if (!disabled) {
        setAnchorEl(e.currentTarget)
      }
    },
    [disabled]
  )

  const handleClose = React.useCallback(() => {
    setQuery('')
    setAnchorEl(null)
    onClose && onClose()
  }, [onClose])

  useKeyListener({ key: 'Escape', callback: handleClose })

  const handleSelect = React.useCallback(
    (opt, e) => {
      setQuery('')
      onSelect(opt, e.target.checked)
      if (useRadio) {
        setAnchorEl(null)
      }
      if (closeOnSelect) {
        handleClose()
      }
    },
    [onSelect, useRadio, closeOnSelect, handleClose]
  )

  React.useEffect(() => {
    if (!Array.isArray(selected)) {
      return
    }

    const unselectedNum = limit - selected.length
    setShowSearch(Boolean(unselectedNum + selected.length >= limit))
  }, [selected, limit])

  const maxNumber = React.useMemo(() => {
    if (!Array.isArray(prevSelected)) {
      return []
    }

    let filtered = filterAgents
      .filter(opt => !prevSelected.includes(opt))
      .map(renderOption)
      .sort(sortFunc)
      .map(x => x?.props.opt.id)
    const unselectedNum = limit - prevSelected.length
    filtered = filtered.slice(0, unselectedNum)

    if (!filtered.length) {
      return <div style={{ marginTop: '0.5rem' }}>No Options</div>
    }

    return filtered.map((opt, index) => {
      const checked = selected.includes(opt)

      return (
        <div key={`${opt}-${index}`} style={{ display: 'flex' }}>
          <Element
            useRadio={useRadio}
            checked={checked}
            onChange={e => handleSelect(opt, e)}
          />
          <span
            onClick={() => handleSelect(opt, { target: { checked: !checked } })}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {renderOption(opt)}
          </span>
        </div>
      )
    })
  }, [
    prevSelected,
    filterAgents,
    renderOption,
    limit,
    useRadio,
    selected,
    handleSelect,
    sortFunc
  ])

  const handleSearch = React.useCallback(
    e => {
      const val = e.target.value
      setQuery(val)
      onSearch && onSearch(val)
    },
    [onSearch]
  )

  React.useEffect(() => {
    if (!search) {
      return
    }

    if (!query) {
      setFilterSelected(prevSelected.filter(Boolean))
      setFilterAgents(options)
      return
    }

    const newOptions = prevSelected
      .map(renderOption)
      .filter(opt =>
        opt?.props.opt.name?.toLowerCase().includes(query.toLowerCase())
      )
      .sort(sortFunc)
      .map(val => options.find(opt => val?.key === opt))

    const unselectedNewOptions = options
      .map(renderOption)
      .filter(opt =>
        opt?.props.opt.name?.toLowerCase().includes(query.toLowerCase())
      )
      .sort(sortFunc)
      .map(val => options.find(opt => val?.key === opt))
      .filter(opt => !prevSelected.includes(opt))

    setFilterSelected(newOptions.filter(Boolean))
    setFilterAgents(unselectedNewOptions)
  }, [query, options, renderOption, selected, search, prevSelected, sortFunc])

  React.useEffect(() => {
    if (!shouldOpen) {
      return
    }

    if (mainRef.current) {
      setAnchorEl(mainRef.current)
      setShouldOpen(false)
    }
  }, [shouldOpen])

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div
        ref={mainRef}
        onClick={handleClick}
        className={clsx(classes.anchor, anchorClass)}
      >
        {children}

        <Popper
          open={open}
          onClose={handleClose}
          anchorEl={anchorEl}
          style={{ zIndex: '1500' }}
          disablePortal={disablePortal}
          modifiers={[
            { name: 'flip' },
            { name: 'offset', options: { offset: [60, 0] } }
          ]}
        >
          <div className={classes.menu}>
            {search && (
              <input
                autoFocus
                placeholder='Search...'
                value={query}
                onChange={e => handleSearch(e)}
                className={classes.input}
              />
            )}

            {TopChildren && (
              <div className={classes.topChildren}>
                <TopChildren onClose={handleClose} />
              </div>
            )}

            {filterSelected.map(opt => {
              const checked = selected.includes(opt)

              return (
                <div style={{ display: 'flex' }} key={opt}>
                  <Element
                    useRadio={useRadio}
                    checked={checked}
                    onChange={e => handleSelect(opt, e)}
                    className={classes.checkBox}
                  />
                  <span
                    onClick={() =>
                      handleSelect(opt, { target: { checked: !checked } })
                    }
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    {renderOption(opt)}
                  </span>
                </div>
              )
            })}

            {Boolean(prevSelected.length) && (
              <Divider style={{ width: '100%' }} />
            )}

            {loading ? (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            ) : (
              maxNumber
            )}
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}

export default CheckboxDropdown
